import { UploadCloud } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type DragEvent } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Props {
  onUpload: (file: File) => Promise<unknown> | void;
  isUploading?: boolean;
  disabled?: boolean;
  error?: string | null;
}

const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';
const MAX_BYTES = 30 * 1024 * 1024;

export function ReportUpload({ onUpload, isUploading, disabled, error }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const preview = useMemo(() => {
    if (!file) return null;
    if (file.type.startsWith('image/')) return URL.createObjectURL(file);
    return null;
  }, [file]);

  const validateAndSet = useCallback((f: File | null): void => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setLocalError('Arquivo maior que 30 MB.');
      return;
    }
    if (!ACCEPT.split(',').includes(f.type) && !f.name.endsWith('.pdf')) {
      setLocalError('Formato não suportado. Envie PDF ou imagem.');
      return;
    }
    setLocalError(null);
    setFile(f);
  }, []);

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0] ?? null;
    validateAndSet(f);
  };

  const submit = async (): Promise<void> => {
    if (!file) return;
    await onUpload(file);
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayError = error ?? localError;

  return (
    <div className="flex flex-col gap-3">
      <div
        role="button"
        tabIndex={0}
        aria-disabled={disabled || undefined}
        aria-label="Área para arrastar arquivo de relatório"
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-(--color-border) p-6 text-center transition-colors',
          dragging && 'border-(--color-accent) bg-(--color-surface-elevated)',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <UploadCloud className="mb-2 h-8 w-8 text-(--color-fg-muted)" aria-hidden />
        <p className="text-sm font-medium">
          {file ? file.name : 'Arraste o relatório ou clique para selecionar'}
        </p>
        <p className="mt-1 text-xs text-(--color-fg-muted)">
          PDF, PNG ou JPG — até 30 MB
        </p>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={ACCEPT}
          disabled={disabled}
          onChange={(e) => validateAndSet(e.target.files?.[0] ?? null)}
        />
      </div>

      {preview ? (
        <img
          src={preview}
          alt="Pré-visualização do relatório"
          className="max-h-64 w-full rounded-md border border-(--color-border) object-contain"
        />
      ) : null}

      {file && file.type === 'application/pdf' ? (
        <p className="text-xs text-(--color-fg-muted)">
          PDF selecionado — pré-visualização indisponível.
        </p>
      ) : null}

      {displayError ? (
        <p role="alert" className="text-sm text-(--color-error)">
          {displayError}
        </p>
      ) : null}

      {file ? (
        <div className="flex items-center gap-2">
          <Button onClick={submit} disabled={isUploading || disabled}>
            {isUploading ? 'Enviando…' : 'Enviar relatório'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setFile(null);
              if (inputRef.current) inputRef.current.value = '';
            }}
            disabled={isUploading}
          >
            Descartar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
